import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Lock, LogOut, Search, Download, RefreshCw, Inbox } from 'lucide-react';
import useSeo from '../hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'interscope_admin_token';

export default function Admin() {
  useSeo({ title: 'Admin · Leads | Interscope Ads', description: 'Interscope Ads admin leads dashboard.' });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch (err) {
      setError(err?.response?.status === 401 ? 'Incorrect password.' : 'Login failed. Try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setLeads([]);
  };

  const fetchLeads = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => [l.name, l.email, l.company, l.industry, l.campaign_type, l.markets, l.message]
      .filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [leads, query]);

  const exportCsv = () => {
    const cols = ['created_at', 'name', 'email', 'phone', 'company', 'website', 'industry', 'budget', 'markets', 'campaign_type', 'message'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [cols.join(',')].concat(filtered.map((l) => cols.map((c) => esc(l[c])).join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interscope-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  if (!token) {
    return (
      <main className="min-h-screen pt-32 pb-20 grid place-items-center px-5">
        <form onSubmit={login} className="glass-strong rounded-3xl p-8 md:p-10 w-full max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] grid place-items-center mb-6"><Lock size={26} className="text-white" /></div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Admin Access</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Sign in to view contact form leads.</p>
          <label className="text-sm text-slate-600">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="mt-2 w-full h-12 rounded-xl bg-slate-100 border border-slate-200 px-4 text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#2563eb]/60"
          />
          {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
          <button type="submit" disabled={loggingIn} className="mt-6 w-full rounded-full bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] px-6 py-3.5 font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60">
            {loggingIn ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-28 pb-20 px-5 md:px-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} of {leads.length} submission{leads.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={fetchLeads} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-[#2563eb]/50 transition-colors"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh</button>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-[#2563eb]/50 transition-colors"><Download size={15} /> Export CSV</button>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-red-400/50 hover:text-red-300 transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, company…" className="w-full h-12 rounded-xl bg-slate-100 border border-slate-200 pl-11 pr-4 text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#2563eb]/60" />
      </div>

      {filtered.length === 0 ? (
        <div className="glass rounded-3xl p-16 text-center">
          <Inbox size={40} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-500">{loading ? 'Loading…' : 'No leads yet. New contact submissions will appear here.'}</p>
        </div>
      ) : (
        <div className="glass-strong rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  {['Date', 'Name', 'Email', 'Company', 'Industry', 'Budget', 'Campaign', 'Markets', 'Message'].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-slate-200 hover:bg-slate-50 align-top">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-4 py-3 text-slate-900 font-medium whitespace-nowrap">{l.name}</td>
                    <td className="px-4 py-3 text-[#2563eb] whitespace-nowrap"><a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a></td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.company || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.industry || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.budget || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.campaign_type || l.role || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.markets || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs"><span className="line-clamp-3">{l.message}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
}
